<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Input } from '@sveltestrap/sveltestrap';

  import { getNotificationsContext } from '@client/components/notifications';
  import { SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { DomainsService } from '@client/services/domains';
  import { SignaturesService } from '@client/services/signatures';
  import { TagBlocksService } from '@client/services/tag-blocks';

  import { userStore } from '@client/stores/user';

  import { generateUniqueRulename } from '@client/utils/rulenames';
  import { sanitizeInputValue } from '@client/utils/sanitize';

  import { debounce } from '@common/utils/debounce';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let loading = true;
  let signatures = [];
  let selectedSignature = null;

  // Clone to Domain specific state
  let domains = [];
  let selectedDomains = [];
  let domainFilterText = '';
  let searchingDomains = false;
  let createNewDomain = false;
  let targetRulename = '';

  // Determine action type from step
  $: actionType = step.action?.value || step.action;
  $: isClone = actionType === 'signature_clone';
  $: isCloneToDomain = actionType === 'signature_clone_domain';
  $: isArchive = actionType === 'signature_archive';
  $: isUnarchive = actionType === 'signature_unarchive';

  const getUserSignature = async (signature) => {
    if (signature.html) {
      return signature.html;
    }

    const data = {
      username: 'adminuser',
      scratchblockname: signature.rulename,
      testData: {
        testsender: 'adminuser'
      }
    };

    try {
      const { rendered } = await SignaturesService.getSignature(data);
      signature.html = rendered;
      return rendered;
    } catch (error) {
      console.error('Failed to load signature preview:', error);
      return '';
    }
  };

  const searchDomains = debounce(async (query) => {
    if (!query || query.length < 2) {
      return;
    }

    searchingDomains = true;

    try {
      const response = await DomainsService.getAll({
        query: query,
        screen: $userStore.isStaff ? 'staff' : '',
        perPage: 100
      });

      const data = Array.isArray(response?.data) ? response.data : [];
      const searchResults = data.map(({ domainname }) => ({ value: domainname, label: domainname }));

      // Merge with existing domains, avoiding duplicates
      const existingValues = new Set(domains.map((d) => d.value));
      const newDomains = searchResults.filter((d) => !existingValues.has(d.value));
      domains = [...domains, ...newDomains];
    } catch (err) {
      console.error('Domain search error:', err);
    }

    searchingDomains = false;
  }, 300);

  const onDomainFilter = (e) => {
    const filteredResults = e.detail;
    createNewDomain = filteredResults.length === 0 && domainFilterText.length;

    // Trigger server-side search when filtering
    if (domainFilterText && domainFilterText.length >= 2) {
      searchDomains(domainFilterText);
    }
  };

  const onDomainKeyUp = (e) => {
    if (e.key === 'Enter') {
      if (createNewDomain) {
        selectedDomains = [
          ...selectedDomains,
          {
            value: domainFilterText,
            label: domainFilterText
          }
        ];

        domainFilterText = '';
      }
    }
  };

  const onRulenameKeyUp = () => {
    targetRulename = sanitizeInputValue(targetRulename);
  };

  const onSelectSignature = (event) => {
    selectedSignature = event.detail;
  };

  const executeAction = async () => {
    if (isClone) {
      // Clone signature
      const rulename = generateUniqueRulename(selectedSignature.rulename);

      const data = {
        rulename,
        htmltemplate: selectedSignature.htmltemplate,
        texttemplate: selectedSignature.texttemplate,
        replyhtmltemplate: selectedSignature.replyhtmltemplate,
        replytexttemplate: selectedSignature.replytexttemplate,
        replytemplateenabled: selectedSignature.replytemplateenabled,
        params: selectedSignature.params
      };

      const response = await TagBlocksService.create(data);

      if (response.status >= 400) {
        notify($format('label.FAILURE.SIGNATURE_CLONE', { values: { rulename: selectedSignature.rulename } }), 'error');
        throw new Error($format('label.FAILURE.SIGNATURE_CLONE'));
      }

      notify($format('label.SUCCESS.CLONE', { values: { type: selectedSignature.rulename } }));

      step.data = {
        id: response.rulename,
        rollbackData: {
          clonedRulename: response.rulename
        }
      };
    } else if (isCloneToDomain) {
      // Clone signature to other domains
      const destdomains = selectedDomains.map(({ value }) => value).join(' ');
      const sourcetagblock = selectedSignature.rulename;
      const targettagblock = targetRulename || selectedSignature.rulename;

      const response = await TagBlocksService.clone({
        cloneOptions: {},
        destdomains,
        sourcedomain: $userStore.domain,
        sourcetagblock,
        targettagblock
      });

      if (response.status >= 400) {
        notify($format('label.FAILURE.CLONE_SIGNATURE_TO_DOMAINS'), 'error');
        throw new Error($format('label.FAILURE.SIGNATURE_CLONE'));
      }

      notify($format('label.SUCCESS.CLONE_SIGNATURE_TO_DOMAINS'));

      step.data = {
        id: targettagblock,
        rollbackData: {
          destdomains: selectedDomains.map(({ value }) => value),
          targettagblock
        }
      };
    } else if (isArchive || isUnarchive) {
      // Archive or unarchive signature
      const previousState = selectedSignature.archived;

      const updatedSignature = {
        ...selectedSignature,
        archived: isArchive ? true : false
      };

      delete updatedSignature.label;
      delete updatedSignature.value;

      const response = await TagBlocksService.save(updatedSignature);

      if (response.status >= 400) {
        const actionLabel = isArchive ? 'ARCHIVE_SIGNATURE' : 'UNARCHIVE';
        notify($format(`label.FAILURE.${actionLabel}`, { values: { rulename: selectedSignature.rulename } }), 'error');
        throw new Error($format(`label.FAILURE.${isArchive ? 'ARCHIVE_SIGNATURE' : 'UNARCHIVE'}`));
      }

      notify($format('label.SUCCESS.ARCHIVE', { values: { type: selectedSignature.rulename } }));

      step.data = {
        id: selectedSignature.rulename,
        rollbackData: {
          signature: selectedSignature,
          previousArchivedState: previousState
        }
      };
    }
  };

  const rollbackAction = async () => {
    if (!step.data?.rollbackData) {
      throw new Error('No rollback data available');
    }

    if (isClone) {
      // Delete the cloned signature
      const { clonedRulename } = step.data.rollbackData;

      const response = await TagBlocksService.delete({ rulename: clonedRulename });

      if (response?.status >= 400) {
        notify($format('label.FAILURE.ROLLBACK_SIGNATURE_CLONE'), 'error');
        throw new Error($format('label.FAILURE.ROLLBACK_SIGNATURE_DELETE'));
      }

      notify($format('label.SUCCESS.ROLLBACK_SIGNATURE_CLONE'));
    } else if (isCloneToDomain) {
      // Rollback not possible for domain clones
      notify($format('label.SUCCESS.ROLLBACK_NOT_AVAILABLE_DOMAIN_CLONES'), 'warning');
      throw new Error('Rollback not available for domain clones');
    } else if (isArchive || isUnarchive) {
      // Restore previous archived state
      const { signature, previousArchivedState } = step.data.rollbackData;

      const restoredSignature = {
        ...signature,
        archived: previousArchivedState
      };

      delete restoredSignature.label;
      delete restoredSignature.value;

      const response = await TagBlocksService.save(restoredSignature);

      if (response?.status >= 400) {
        notify($format('label.FAILURE.ROLLBACK_ARCHIVE_STATE'), 'error');
        throw new Error($format('label.FAILURE.ROLLBACK_SIGNATURE_DELETE'));
      }

      notify($format('label.SUCCESS.ROLLBACK_ARCHIVE_STATE'));
    }

    return { success: true };
  };

  onMount(async () => {
    const allSignatures = await TagBlocksService.getAll();

    // Filter based on action type
    if (isUnarchive) {
      // Show only archived signatures for unarchive
      signatures = allSignatures
        .filter(({ archived }) => archived)
        .map((sig) => ({
          label: sig.name || sig.rulename,
          value: sig.rulename,
          ...sig
        }));
    } else {
      // Show non-archived signatures for clone and archive
      signatures = allSignatures
        .filter(({ archived }) => !archived)
        .map((sig) => ({
          label: sig.name || sig.rulename,
          value: sig.rulename,
          ...sig
        }));
    }

    // Load initial domains for clone to domain action
    if (isCloneToDomain) {
      try {
        const response = await DomainsService.getAll({
          screen: $userStore.isStaff ? 'staff' : '',
          perPage: 100
        });

        const data = Array.isArray(response?.data) ? response.data : [];
        domains = data.map(({ domainname }) => ({ value: domainname, label: domainname }));
      } catch (err) {
        console.error('Failed to load initial domains:', err);
      }
    }

    loading = false;

    if (onExecute) {
      onExecute(executeAction);
    }

    if (onRollback) {
      onRollback(rollbackAction);
    }
  });

  $: step.data = selectedSignature ? { ...selectedSignature } : {};
  $: {
    if (isCloneToDomain) {
      nextDisabled = !selectedSignature || selectedDomains.length === 0;
    } else {
      nextDisabled = !selectedSignature;
    }
  }
</script>

<div class="signature-actions-form">
  {#if loading}
    <div class="loading">
      <Spinner show inline />
    </div>
  {:else}
    <SectionItem
      label="Select Signature"
      description={isCloneToDomain
        ? 'Choose the signature you want to clone to other domains'
        : isClone
          ? 'Choose the signature you want to clone'
          : isArchive
            ? 'Choose the signature you want to archive'
            : 'Choose the signature you want to unarchive'}
    >
      <Select
        bind:items={signatures}
        bind:value={selectedSignature}
        placeholder="Select a signature..."
        clearable={false}
        showChevron
        on:change={onSelectSignature}
      />
    </SectionItem>

    {#if selectedSignature}
      <SectionItem label="Preview">
        <div class="signature-preview">
          {#if !selectedSignature.html}
            {#await getUserSignature(selectedSignature)}
              <div class="loading">
                <Spinner show inline />
              </div>
            {:then html}
              {#if html}
                <iframe
                  tabindex="-1"
                  style="width: 100%; border: none;"
                  scrolling="no"
                  sandbox="allow-same-origin"
                  title={selectedSignature.name || selectedSignature.rulename}
                  srcdoc="<html><body><div style='transform: scale(.6,.6); transform-origin: left top; -moz-transform: scale(.6,.6); -moz-transform-origin: left top;'>{html ||
                    ''}</div></body></html>"
                />
              {:else}
                <div class="no-preview">
                  <p>No preview available</p>
                </div>
              {/if}
            {:catch error}
              <div class="error-preview">
                <p>Failed to load preview</p>
              </div>
            {/await}
          {:else}
            <iframe
              tabindex="-1"
              style="width: 100%; border: none;"
              scrolling="no"
              sandbox="allow-same-origin"
              title={selectedSignature.name || selectedSignature.rulename}
              srcdoc="<html><body><div style='transform: scale(.6,.6); transform-origin: left top; -moz-transform: scale(.6,.6); -moz-transform-origin: left top;'>{selectedSignature.html ||
                ''}</div></body></html>"
            />
          {/if}
        </div>
      </SectionItem>

      {#if isCloneToDomain}
        <SectionItem label="Select Domains" description="Choose the domains to clone the signature to">
          <div on:keyup={onDomainKeyUp} role="button" tabindex="0">
            <Select
              bind:items={domains}
              bind:value={selectedDomains}
              bind:filterText={domainFilterText}
              placeholder="Search and select domains..."
              multiple
              searchable
              clearable={false}
              showChevron
              on:filter={onDomainFilter}
            >
              <div slot="empty" class="empty">
                {#if searchingDomains}
                  <Spinner show inline />
                {:else if createNewDomain}
                  Press Enter to add domain: {domainFilterText}
                {:else}
                  No domains match
                {/if}
              </div>
            </Select>
          </div>
        </SectionItem>

        <SectionItem label="Rulename (Optional)" description="Specify a new rulename for the cloned signature">
          <Input
            type="text"
            bind:value={targetRulename}
            placeholder={selectedSignature.rulename}
            on:keyup={onRulenameKeyUp}
          />
        </SectionItem>
      {/if}
    {/if}
  {/if}
</div>

<style lang="scss">
  .signature-actions-form {
    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      padding: var(--padding-large);
    }

    :global(.section-item .row.align-items-center) {
      align-items: flex-start !important;
    }

    .signature-preview {
      background-color: var(--color-white);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-base);
      min-height: 100px;
      padding: var(--padding-small);

      iframe {
        min-height: 100px;
      }

      .loading {
        align-items: center;
        display: flex;
        justify-content: center;
        min-height: 100px;
        padding: var(--padding-large);
      }

      .no-preview,
      .error-preview {
        align-items: center;
        color: var(--color-gray-600);
        display: flex;
        justify-content: center;
        min-height: 100px;
        padding: var(--padding-base);

        p {
          margin: 0;
        }
      }

      .error-preview {
        color: var(--color-red-600);
      }
    }

    .empty {
      color: var(--color-gray-600);
      padding: var(--padding-xlarge);
      text-align: center;
    }
  }
</style>
